/**
 * OWNER-OPERATING-SYSTEM-V1 -- Reports (/dashboard/reports).
 *
 * ARCHETYPE: REPORT (F6 -- exception-first: the page LEADS with what is
 * wrong, then analysis sections as NATIVE disclosure regions, first open,
 * the rest closed; polish layer 25.3/25.5). Deep analysis lives HERE per
 * the HOME RULING; Home and Reports never repeat each other's KPI content.
 *
 * REAL WIRES ONLY, all pre-existing and owner-fenced server-side:
 *   funnel snapshot (commercial + sources + attribution), owner dashboard
 *   (clinics + MIS + billing compliance -- the re-homed Overview reads),
 *   THE ONE leakage classifier (exception counts), marketing spend (the
 *   SEPARATE labeled spend stream; the spend-revenue join stays absent by
 *   design), walk-in sources (the folded Analytics read, audit row 18),
 *   attribution/bridge health (audit rows 15/16, SIMPLIFIED to one honest
 *   figure each -- no person names, no tile sprawl).
 *
 * Each section is fault-isolated: one failing wire renders its own honest
 * failure state; the page never blanks (the owner-page posture).
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import {
  fetchOwnerDashboard,
  fetchOwnerFunnelSnapshot,
  fetchOwnerHome,
  fetchOwnerMarketingSpend,
  fetchOwnerSourceBreakdown,
  fetchSlaLeakage,
  fetchOwnerCampaignLeadAttributionHealth,
  fetchOwnerLeadWalkinBridgeHealth,
} from '@/dashboard/api';
import type {
  CampaignLeadAttributionHealth,
  ClinicPerformanceRow,
  DashboardSummary,
  LeadWalkinBridgeHealth,
  OwnerHomeWire,
  OwnerMarketingSpend,
  OwnerSourceBreakdown,
} from '@/dashboard/types';
import type { SlaLeakageResult } from '@/dashboard/sla-leakage/sla-leakage-view';
import { useClinicLens } from '@/dashboard/clinic-context';
import type {
  OwnerFunnelRangePreset,
  OwnerFunnelSnapshot,
} from '@/dashboard/owner/owner-funnel-view';
import {
  formatCount,
  formatInr,
  formatPct,
  metricCellText,
} from '@/dashboard/owner/owner-funnel-view';
import {
  AcBadge,
  DataTable,
  EmptyState,
  KpiBand,
  LoadingState,
  ClinicScopeLabel,
  PageHeader,
  SeamChip,
  SectionBand,
  SelectorBar,
  type DataTableColumn,
  type KpiBandCellDef,
  type SelectorControlDef,
} from '@/dashboard/ui';
import {
  clinicTableRows,
  dataQualityCells,
  exceptionCards,
  leakageCountLine,
  rupeesLabel,
  LIVE_NOW_BASIS,
  TODAY_ONLY_BASIS,
  type ClinicTableRowView,
} from './owner-reports-view';

const RANGE_OPTIONS: readonly { value: OwnerFunnelRangePreset; label: string }[] = [
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '3m', label: 'Last 3 months' },
  { value: '6m', label: 'Last 6 months' },
];

interface WireState<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
}

/**
 * `ready` (SHIP-LANE-1 OWNER TRUTH CORRECTIONS, FIX 1) defaults to true and
 * exists for the reads whose FILTER is itself the product of another read:
 * the selector window is resolved server-side and echoed on the funnel
 * snapshot, so firing a dependent read before that snapshot settles would
 * spend a round trip AND paint an UNFILTERED answer under a selector bar that
 * claims to govern it -- the exact lie this lane exists to remove. While
 * `ready` is false the band holds its loading state and no request is made;
 * the moment the funnel settles (data OR error) the read fires exactly once.
 */
function useWire<T>(
  loader: () => Promise<T>,
  deps: readonly unknown[],
  ready = true,
): WireState<T> {
  const [state, setState] = useState<WireState<T>>({ data: null, error: null, loading: true });
  useEffect(() => {
    let cancelled = false;
    setState({ data: null, error: null, loading: true });
    if (!ready) return;
    loader()
      .then((data) => {
        if (!cancelled) setState({ data, error: null, loading: false });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setState({
            data: null,
            error: err instanceof Error ? err.message : 'Load failed',
            loading: false,
          });
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, ready]);
  return state;
}

function SectionFallback({
  state,
  what,
}: {
  readonly state: WireState<unknown>;
  readonly what: string;
}): JSX.Element {
  if (state.loading) return <LoadingState label={`Loading ${what}`} lines={2} />;
  return (
    <EmptyState
      title={`${what} did not load.`}
      body={`${state.error ?? 'The read failed.'} Your other sections are unaffected -- reload the page to retry.`}
    />
  );
}

function clinicColumns(): DataTableColumn<ClinicTableRowView>[] {
  return [
    { key: 'clinic', header: 'Clinic', render: (r) => <strong>{r.clinicName}</strong> },
    {
      key: 'collections',
      header: 'Collections',
      align: 'end',
      render: (r) => r.recordedCollections,
    },
    { key: 'mis', header: 'MIS revenue', align: 'end', render: (r) => r.misRevenue },
    { key: 'patients', header: 'Patients', align: 'end', render: (r) => r.patients },
    { key: 'walkins', header: 'Walk-ins', align: 'end', render: (r) => r.walkins },
    { key: 'noshow', header: 'No-show', align: 'end', render: (r) => r.noShow },
    {
      key: 'billing',
      header: 'Billing',
      render: (r) => (
        <AcBadge tone={r.billing === 'submitted' ? 'positive' : 'pending'}>
          {r.billing === 'submitted' ? 'Submitted' : 'Pending'}
        </AcBadge>
      ),
    },
  ];
}

export default function OwnerReportsPage(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const rangeParam = searchParams.get('range');
  const range: OwnerFunnelRangePreset =
    rangeParam === '7d' || rangeParam === '3m' || rangeParam === '6m' ? rangeParam : '30d';
  // 119-CLINIC-OPERATING-SPINE-V1 (Phase 2, founder ruling 4b). Reports is an
  // ANALYTICS surface: its ?clinicId is a REPORT PARAMETER, not the LENS. It
  // DEFAULTS TO THE LENS when unset, STATES the divergence on the glass (the
  // scope slot below), is validated server-side against the SAME reach list the
  // lens is built from (ownerClinicIds), and NEVER rewrites the lens -- it
  // writes only the URL, so a shared link cannot move someone's session.
  const clinicCtx = useClinicLens();
  const lensClinicId = clinicCtx?.selectedClinicId ?? null;
  const clinicParam =
    searchParams.get('clinicId') ??
    (lensClinicId !== null ? String(lensClinicId) : null);

  const funnel = useWire<OwnerFunnelSnapshot>(
    () =>
      fetchOwnerFunnelSnapshot({
        range,
        ...(clinicParam ? { clinicId: clinicParam } : {}),
      }),
    [range, clinicParam],
  );
  const dashboard = useWire<{ summary: DashboardSummary; clinics: ClinicPerformanceRow[] }>(
    () => fetchOwnerDashboard(),
    [],
  );
  // SHIP-LANE-1 OWNER TRUTH CORRECTIONS (FIX 1). The selector bar governed
  // only the funnel wire; five bands read `[]` and never moved when the owner
  // moved the date range or the clinic. The window below is the ONE the SERVER
  // resolved for the selected preset (owner-funnel.ts echoes it back as
  // `range.from` / `range.to`), so the filtered bands are matched to the
  // Commercial band's window by construction and no second range resolver is
  // minted client-side. Absent (funnel still loading or failed) means the
  // dependent read runs on its own endpoint default -- an honest, unfiltered
  // answer, never a blank band: fault isolation is preserved.
  // Settled, not successful: on a funnel FAILURE the window is null and the
  // dependent read runs on its own endpoint default -- an honest, unfiltered
  // answer, never a blank band. Fault isolation is preserved.
  const windowReady = !funnel.loading;
  const windowFrom = funnel.data?.range.from ?? null;
  const windowTo = funnel.data?.range.to ?? null;

  // DEFINITIONALLY-TODAY: leakage is the OPEN work queue computed on read
  // (getSlaLeakage takes no window at all) -- rewiring it to a 6-month range
  // would be a lie. It carries a label instead (see the R1 band).
  const leakage = useWire<SlaLeakageResult>(() => fetchSlaLeakage(), []);
  const spend = useWire<OwnerMarketingSpend>(
    () =>
      fetchOwnerMarketingSpend({
        ...(windowFrom ? { from: windowFrom } : {}),
        ...(windowTo ? { to: windowTo } : {}),
      }),
    [windowFrom, windowTo],
    windowReady,
  );
  // CANNOT: /api/owner/analytics hard-pins its own 7-day window inside
  // getOwnerSourceBreakdown and its handler parses no query at all, so there
  // is no parameter to pass. Its chip states the 7d window on the glass.
  const walkinSources = useWire<OwnerSourceBreakdown>(() => fetchOwnerSourceBreakdown(), []);
  const attribution = useWire<CampaignLeadAttributionHealth>(
    () =>
      fetchOwnerCampaignLeadAttributionHealth({
        ...(windowFrom ? { from: windowFrom } : {}),
        ...(windowTo ? { to: windowTo } : {}),
        ...(clinicParam ? { clinicId: clinicParam } : {}),
      }),
    [windowFrom, windowTo, clinicParam],
    windowReady,
  );
  const bridge = useWire<LeadWalkinBridgeHealth>(
    () =>
      fetchOwnerLeadWalkinBridgeHealth({
        ...(windowFrom ? { from: windowFrom } : {}),
        ...(windowTo ? { to: windowTo } : {}),
        ...(clinicParam ? { clinicId: clinicParam } : {}),
      }),
    [windowFrom, windowTo, clinicParam],
    windowReady,
  );
  // Selector vocabulary (clinics + saved views) rides the Home wire's
  // config-backed lists -- ONE source for the approved options.
  const home = useWire<OwnerHomeWire>(() => fetchOwnerHome({ range: 'yesterday' }), []);

  const setParam = (key: string, value: string | null): void => {
    const next = new URLSearchParams(searchParams);
    if (value === null || value === '') next.delete(key);
    else next.set(key, value);
    setSearchParams(next, { replace: true });
  };

  const controls: SelectorControlDef[] = useMemo(() => {
    const clinics = home.data?.clinics ?? [];
    return [
      {
        key: 'range',
        label: 'Date range',
        options: RANGE_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
        value: range,
        onChange: (v) => setParam('range', v),
      },
      {
        key: 'scope',
        label: 'Clinic scope',
        options: [
          { value: '', label: 'All clinics' },
          ...clinics.map((c) => ({ value: String(c.clinicId), label: c.name })),
        ],
        value: clinicParam ?? '',
        onChange: (v) => setParam('clinicId', v === '' ? null : v),
      },
      {
        key: 'department',
        label: 'Department',
        options: [{ value: 'all', label: 'All departments' }],
        value: 'all',
      },
      {
        key: 'trend',
        label: 'Trend view',
        options: [{ value: 'weekly', label: 'Trend: weekly buckets' }],
        value: 'weekly',
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [home.data, range, clinicParam, searchParams]);

  const cards = exceptionCards(leakage.data, dashboard.data?.summary ?? null);

  const commercialCells: KpiBandCellDef[] = useMemo(() => {
    const f = funnel.data;
    if (!f) return [];
    const cells: KpiBandCellDef[] = [
      {
        key: 'converted',
        label: f.kpis.convertedToPatient.label,
        value: formatCount(f.kpis.convertedToPatient.value),
        meta: 'first active payment = conversion',
      },
      {
        key: 'conversion-rate',
        label: f.kpis.conversionRate.label,
        value: formatPct(f.kpis.conversionRate.value),
        meta: `${formatCount(f.kpis.totalLeads.value)} leads in window`,
      },
    ];
    if (f.recordedCollections) {
      cells.push({
        key: 'collections',
        label: f.recordedCollections.total.label,
        value: formatInr(f.recordedCollections.total.value),
        meta: `${f.recordedCollections.transactionCount} payments -- ${f.recordedCollections.distinctPaidVisits} paid visits`,
      });
      cells.push({
        key: 'avg-paid-visit',
        label: f.recordedCollections.averagePerPaidVisit.label,
        value: formatInr(f.recordedCollections.averagePerPaidVisit.value),
        meta: 'clinic grain (per-doctor needs money attribution)',
      });
    }
    return cells;
  }, [funnel.data]);

  const qualityCells = dataQualityCells(attribution.data, bridge.data);

  return (
    <div>
      <PageHeader
        title="Reports"
        subs={[
          funnel.data ? funnel.data.range.timezoneNote : undefined,
          funnel.data ? funnel.data.stateCopy.attributionWindowRule : undefined,
        ]}
        scope={
          <ClinicScopeLabel
            label={
              clinicParam === null
                ? 'All clinics'
                : ((home.data?.clinics ?? []).find(
                    (c) => String(c.clinicId) === clinicParam,
                  )?.name ?? 'Unnamed clinic')
            }
            mode="parameter"
            lensLabel={clinicCtx?.selectedLabel ?? null}
            testId="owner-reports-clinic-scope"
          />
        }
      />
      <div style={{ margin: '0 0 var(--ac-space-4)' }}>
        <SelectorBar controls={controls} testId="owner-reports-selbar" />
      </div>

      <div className="ac-band-flow">
        {/* R1 -- exception band (never collapses; quiet = healthy) */}
        <SectionBand
          title="Needs review"
          basis={
            leakage.data
              ? `${leakageCountLine(leakage.data)} -- ${LIVE_NOW_BASIS}`
              : LIVE_NOW_BASIS
          }
          action={<Link to="/dashboard/leakage">Leakage board</Link>}
          testId="owner-reports-exceptions"
        >
          {cards.length > 0 ? (
            <div className="ac-opsrow">
              {cards.map((c) => (
                <Link key={c.key} to={c.to} className="ac-selbar__chip">
                  <strong>{c.count}</strong> {c.title} -- {c.detail}
                </Link>
              ))}
            </div>
          ) : leakage.loading || dashboard.loading ? (
            <LoadingState label="Loading exceptions" lines={1} />
          ) : (
            <EmptyState
              title="Nothing needs review."
              body="No open leakage items and no clinic flags today."
            />
          )}
        </SectionBand>

        {/*
          OWNER-DAILY-EOD-DISCOVERY-P1 (founder order 2026-08-18): the daily /
          EOD money console door, PROMOTED to an always-visible entry. It was a
          chip buried inside the collapsed "Marketing" band (SectionBand
          collapsible + no defaultOpen renders <details> closed) -- the owner
          landed on Reports and never saw the door, and reaching it cost an
          expand + a click. This STATIC band never collapses, so the owner
          immediately sees a clear "Daily / EOD Report" entry and opens the
          EXISTING console in ONE click. REUSES the page's SectionBand +
          ac-opsrow + ac-selbar__chip Link pattern (R1 exception cards / the
          former Marketing chip) -- no new route, no new nav family, no new
          report. Route + server owner-fence UNCHANGED (a nav change is never a
          permission change -- 06-CRM-ROLE-VISIBILITY-DOCTRINE rule 2; the door
          lives under Reports, so no seventh owner nav row -- rule 3).
        */}
        <SectionBand
          title="Daily / EOD Report"
          basis="Walk-ins, conversion, treatments, revenue, clinic performance"
          testId="owner-reports-eod"
        >
          <div className="ac-opsrow">
            <Link
              to="/dashboard/reports/money-console"
              className="ac-selbar__chip"
              data-testid="omc-door"
            >
              Open the Money Console
            </Link>
          </div>
        </SectionBand>

        {/* R2 -- commercial (FIRST section, open) */}
        <SectionBand title="Commercial" collapsible defaultOpen testId="owner-reports-commercial">
          {funnel.data ? (
            <KpiBand
              title={`Leads to revenue -- ${funnel.data.range.from} to ${funnel.data.range.to}`}
              cells={commercialCells}
            />
          ) : (
            <SectionFallback state={funnel} what="Commercial KPIs" />
          )}
        </SectionBand>

        {/* R3 -- data quality (separate from commercial; honest states) */}
        <SectionBand title="Data quality" collapsible testId="owner-reports-quality">
          {qualityCells.length > 0 ? (
            <KpiBand
              title="Can the numbers be trusted"
              cells={qualityCells.map((c) => ({
                key: c.key,
                label: c.label,
                value: c.value,
                meta: c.meta,
              }))}
            />
          ) : attribution.loading || bridge.loading ? (
            <LoadingState label="Loading data quality" lines={2} />
          ) : (
            <EmptyState
              title="Data-quality reads did not load."
              body="Attribution and bridge health are temporarily unavailable; other sections are unaffected."
            />
          )}
          {funnel.data?.revenueNotLinked ? (
            <p className="ac-opsrow">
              <SeamChip
                label={`${formatInr(funnel.data.revenueNotLinked.value)} collected, not linked to any lead`}
                explainer={funnel.data.revenueNotLinked.tooltip}
              />
            </p>
          ) : null}
        </SectionBand>

        {/* R4 -- clinics: THE table primitive exemplar (audit row 19 executed) */}
        <SectionBand
          title="Clinics"
          basis={
            dashboard.data
              ? `${dashboard.data.clinics.length} clinics -- flagged first -- ${TODAY_ONLY_BASIS}`
              : TODAY_ONLY_BASIS
          }
          collapsible
          testId="owner-reports-clinics"
        >
          {dashboard.data ? (
            <DataTable
              columns={clinicColumns()}
              rows={clinicTableRows(dashboard.data.clinics)}
              rowKey={(r) => r.clinicId}
              rowClassName={(r) => (r.flagged ? 'row--flagged' : undefined)}
              stickyHeader
              countLine={`1-${dashboard.data.clinics.length} of ${dashboard.data.clinics.length} clinics -- today's MIS + live collections; '-' means no rollup received (never a fake zero)`}
              empty={
                <EmptyState
                  title="No clinics in scope."
                  body="This organization has no clinics registered yet."
                />
              }
              testId="owner-reports-clinics-table"
            />
          ) : (
            <SectionFallback state={dashboard} what="The clinics table" />
          )}
        </SectionBand>

        {/* R5 -- money & MIS (the re-homed Overview money reads) */}
        <SectionBand
          title="Money and MIS"
          basis={TODAY_ONLY_BASIS}
          collapsible
          testId="owner-reports-money"
        >
          {dashboard.data ? (
            <KpiBand
              title={`MIS lane -- ${dashboard.data.summary.reportDate}`}
              cells={[
                {
                  key: 'mis-revenue',
                  label: 'MIS-submitted revenue',
                  value: dashboard.data.summary.misRollup?.receivedForDate
                    ? rupeesLabel(dashboard.data.summary.totalRevenue)
                    : 'No rollup yet',
                  meta: dashboard.data.summary.misRollup?.receivedEver
                    ? 'for today; clinics submit through the day'
                    : 'the MIS pipeline has not fed this org yet',
                },
                {
                  key: 'billing-compliance',
                  label: 'Billing compliance',
                  value: dashboard.data.summary.misRollup?.receivedEver
                    ? `${Math.round(dashboard.data.summary.billingCompliance * 100)}%`
                    : 'Not available',
                  meta: 'clinics with a submitted rollup today',
                },
                {
                  key: 'collections-today',
                  label: 'Collections recorded today',
                  value:
                    dashboard.data.summary.collections == null
                      ? 'Not available'
                      : rupeesLabel(
                          dashboard.data.summary.collections.totalMinorUnits / 100,
                        ),
                  meta:
                    dashboard.data.summary.collections == null
                      ? 'money read unavailable right now'
                      : `live IST clinic day -- ${dashboard.data.summary.collections.transactionCount} payments`,
                },
              ]}
            />
          ) : (
            <SectionFallback state={dashboard} what="Money and MIS" />
          )}
        </SectionBand>

        {/* R6 -- marketing (sources + the SEPARATE spend stream + walk-in sources) */}
        <SectionBand title="Marketing" collapsible testId="owner-reports-marketing">
          {funnel.data ? (
            <DataTable
              columns={[
                { key: 'source', header: 'Source', render: (r: OwnerFunnelSnapshot['sourceTable'][number]) => r.sourceLabel },
                { key: 'leads', header: 'Leads', align: 'end', render: (r) => metricCellText(r.leads, 'count') },
                { key: 'contacted', header: 'Contacted', align: 'end', render: (r) => metricCellText(r.contactedPct, 'pct') },
                { key: 'appt', header: 'Appointments', align: 'end', render: (r) => metricCellText(r.appointmentPct, 'pct') },
                { key: 'conv', header: 'Converted', align: 'end', render: (r) => metricCellText(r.conversionPct, 'pct') },
              ]}
              rows={funnel.data.sourceTable}
              rowKey={(r) => r.sourceNormalized}
              stickyHeader
              countLine={`${funnel.data.sourceTable.length} sources -- source-to-revenue drill has the money view`}
              onRowClick={() => navigate('/dashboard/reports/source-to-revenue')}
              empty={
                <EmptyState
                  title="No leads in this window."
                  body="Source performance appears once leads exist in the selected range."
                />
              }
              testId="owner-reports-sources"
            />
          ) : (
            <SectionFallback state={funnel} what="Source performance" />
          )}
          <div className="ac-opsrow">
            <Link to="/dashboard/reports/source-to-revenue" className="ac-selbar__chip">
              Source to revenue drill
            </Link>
            {/* OWNER-DAILY-EOD-DISCOVERY-P1: the money console door RELOCATED
                out of this collapsed Marketing band to the always-visible
                "Daily / EOD Report" band near the top of the page (it was
                undiscoverable here). ONE door only -- not duplicated. */}
            {spend.data ? (
              <SeamChip
                label={`Ad spend (separate stream): Rs ${spend.data.totals.spend_inr.toLocaleString('en-IN')} -- ${spend.data.totals.campaign_count} campaigns`}
                explainer={`Google Ads only; ${spend.data.caveats.join(' ')} Spend and revenue stay separate labeled streams until the join is built lawfully.`}
              />
            ) : null}
            {walkinSources.data ? (
              <SeamChip
                label={`Walk-in sources (7d): ${walkinSources.data.totalWalkins} walk-ins across ${walkinSources.data.bySource.length} sources`}
                explainer="The folded Analytics read (walk-in source split, last 7 days). Row grain rides the source table above as capture improves."
              />
            ) : null}
          </div>
        </SectionBand>
      </div>
    </div>
  );
}
